import type { Span } from "@opentelemetry/api";

import { createLogger } from "@/logger";
import {
  feedItemsBackfilled,
  onFeedItemPageLanded,
  syncFeedBatches,
  syncFeedItems,
} from "@/models/feedItem/feedItemSync";
import { actions } from "@/models/TelemetryProvider";
import { warmupCandidateFeedIds } from "./warmupCandidates";
import type { WarmupSettings } from "./warmupConstants";

const log = createLogger("warmup");

type WarmupStats = {
  feedsWarmedUp: number;
  itemsLoaded: number;
  contentDownloaded: number;
  durationMs: number;
};

type TrackAction = (props: { action: string; target?: string }, customAttributes?: Record<string, unknown>) => Span;
type FinishAction = (props: { action: string; target?: string }, customAttributes?: Record<string, unknown>) => void;

/**
 * Loads the last `web.warmup.lookbackHours` of items of the feeds a member will open, right after bootstrap, so the
 * channels they open, and the Dispatch page's unread counts, read from the store.
 *
 * The item event log replaces a request per feed. Under `limited` and `all` the feeds `warmupCandidateFeedIds` picks
 * are read `feedsPerBatch` at a time through the log's `feedIds` lens, so each read costs those feeds' items; under
 * `workspace` one walk reads every readable feed at once. A batch loads its whole lookback in one request; the
 * `workspace` walk lands the last twelve hours first and the rest of the lookback behind them. Either way this call
 * resolves once the whole lookback has landed, so a boot that waits on it holds it. A warm boot fetches only what was
 * created since the last walk: always under `workspace`, and in batches while the pick cuts into the same batches,
 * since a batch's coverage is keyed by its feeds and a shifted batch walks its lookback again. Items only: content
 * still loads when an item is shown. The walk queues each written
 * feed's unread pass as its page lands. `feedsWarmedUp` and `itemsLoaded` count what landed before this call resolved;
 * `contentDownloaded` stays 0.
 */
export async function warmupFeedItems({
  workspaceId,
  accountId,
  membershipId,
  settings,
  dispatchEnabled = false,
  trackAction,
  finishAction,
}: {
  workspaceId: string;
  accountId: string;
  membershipId: string;
  settings: WarmupSettings;
  /** Widens a `limited` pick to every driver channel, which the Dispatch page counts from. */
  dispatchEnabled?: boolean;
  trackAction?: TrackAction;
  finishAction?: FinishAction;
}): Promise<WarmupStats> {
  const startTime = Date.now();
  const stats: WarmupStats = { feedsWarmedUp: 0, itemsLoaded: 0, contentDownloaded: 0, durationMs: 0 };
  const warmedFeedIds = new Set<string>();
  const ctx = { workspaceId, accountId };

  log.info("[WARMUP] Starting feed warmup", { workspaceId, membershipId, ...settings, dispatchEnabled });
  trackAction?.(actions.warmupFull(), { workspaceId, membershipId, ...settings, dispatchEnabled });

  const stopCounting = onFeedItemPageLanded(({ feedIds, itemCount }) => {
    for (const feedId of feedIds) warmedFeedIds.add(feedId);
    stats.itemsLoaded += itemCount;
  });
  try {
    if (settings.mode === "workspace") {
      trackAction?.(actions.warmupFetchItems(), { workspaceId });
      await syncFeedItems(ctx, settings);
    } else {
      trackAction?.(actions.warmupCalculate(), { workspaceId, membershipId });
      const picked = warmupCandidateFeedIds({
        workspaceId,
        workspaceMembershipId: membershipId,
        settings,
        dispatchEnabled,
      });
      finishAction?.(actions.warmupCalculate(), {
        totalFeeds: picked.feedIds.length,
        unreadCount: picked.unreadCount,
        activityCount: picked.activityCount,
        aliasCount: picked.aliasCount,
        totalUnique: picked.totalUnique,
        alreadyLoaded: picked.alreadyLoaded,
      });
      trackAction?.(actions.warmupFetchItems(), { workspaceId, feedCount: picked.feedIds.length });
      if (picked.feedIds.length > 0) await syncFeedBatches(ctx, picked.feedIds, settings);
    }
    // The walk resolves with the recent window landed; the rest of the lookback lands behind it.
    await feedItemsBackfilled(ctx, settings);
    stats.feedsWarmedUp = warmedFeedIds.size;
    stats.durationMs = Date.now() - startTime;
    finishAction?.(actions.warmupFetchItems(), { itemCount: stats.itemsLoaded, feedCount: stats.feedsWarmedUp });
    log.info("[WARMUP] Completed feed warmup", stats);
    finishAction?.(actions.warmupFull(), stats);
    return stats;
  } catch (error) {
    stats.feedsWarmedUp = warmedFeedIds.size;
    stats.durationMs = Date.now() - startTime;
    log.error("[WARMUP] Feed warmup failed", { error, stats });
    finishAction?.(actions.warmupFetchItems(), { error: String(error) });
    finishAction?.(actions.warmupFull(), { ...stats, error: String(error) });
    return stats;
  } finally {
    stopCounting();
  }
}
